/**
 * Calendar Client
 * Calendar booking operations
 */

import type { CalendarBooking } from './types';
import { API_URL } from './constants';
import { createHeaders, handleApiError, logApiCall } from './base-client';

// =============================================================================
// Calendar Operations
// =============================================================================

/**
 * Get calendar bookings with optional filters
 * Returns bookings sorted by scheduled time.
 *
 * @param businessId - Optional business ID filter
 * @param startDate - Optional start date filter
 * @param endDate - Optional end date filter
 * @returns List of calendar bookings
 *
 * @example
 * ```ts
 * // Get all bookings
 * const bookings = await getCalendarBookings();
 *
 * // Get bookings for a business
 * const businessBookings = await getCalendarBookings('biz-001');
 *
 * // Get bookings for a date range
 * const weekBookings = await getCalendarBookings(
 *   undefined,
 *   new Date('2024-01-01'),
 *   new Date('2024-01-07')
 * );
 * ```
 */
export async function getCalendarBookings(
  businessId?: string,
  startDate?: Date,
  endDate?: Date
): Promise<CalendarBooking[]> {
  // API requires businessId — return empty early to avoid 400
  if (!businessId) return [];

  const startTime = Date.now();

  try {
    const params = new URLSearchParams();
    if (businessId) params.append('businessId', businessId);
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const endpoint = '/api/operator/calendar';
    const url = params.toString()
      ? `${API_URL}${endpoint}?${params.toString()}`
      : `${API_URL}${endpoint}`;

    const response = await fetch(url, {
      headers: createHeaders(),
    });

    logApiCall(endpoint, 'GET', Date.now() - startTime, response.status);

    if (!response.ok) {
      if (response.status === 403) {
        return [];
      }
      throw new Error(`API error: ${response.status}`);
    }

    const json = await response.json();
    // API returns { success, bookings: [...] }
    const bookings = json.bookings || json.data || json;
    if (!Array.isArray(bookings)) return [];

    // Convert date strings to Date objects
    return bookings.map((booking: CalendarBooking & { scheduledAt: string; endsAt: string }) => ({
      ...booking,
      scheduledAt: new Date(booking.scheduledAt),
      endsAt: new Date(booking.endsAt),
    }));
  } catch (error) {
    handleApiError(error, 'getCalendarBookings', { businessId });
    return [];
  }
}

/**
 * Get bookings for today
 *
 * @param businessId - Optional business ID filter
 * @returns Today's bookings
 */
export async function getTodayBookings(businessId?: string): Promise<CalendarBooking[]> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return getCalendarBookings(businessId, today, tomorrow);
}

/**
 * Get bookings for the current week
 *
 * @param businessId - Optional business ID filter
 * @returns This week's bookings (starting from today)
 */
export async function getWeekBookings(businessId?: string): Promise<CalendarBooking[]> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const weekEnd = new Date(today);
  weekEnd.setDate(weekEnd.getDate() + 7);

  return getCalendarBookings(businessId, today, weekEnd);
}

/**
 * Get upcoming bookings (next 30 days)
 *
 * @param businessId - Optional business ID filter
 * @returns Upcoming bookings
 */
export async function getUpcomingBookings(businessId?: string): Promise<CalendarBooking[]> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const futureDate = new Date(today);
  futureDate.setDate(futureDate.getDate() + 30);

  return getCalendarBookings(businessId, today, futureDate);
}

/**
 * Get past bookings (last 30 days)
 *
 * @param businessId - Optional business ID filter
 * @returns Past bookings
 */
export async function getPastBookings(businessId?: string): Promise<CalendarBooking[]> {
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const pastDate = new Date(today);
  pastDate.setDate(pastDate.getDate() - 30);

  return getCalendarBookings(businessId, pastDate, today);
}

/**
 * Get bookings by status
 *
 * @param status - Booking status to filter by
 * @param businessId - Optional business ID filter
 * @returns Bookings with the specified status
 */
export async function getBookingsByStatus(
  status: CalendarBooking['status'],
  businessId?: string
): Promise<CalendarBooking[]> {
  const bookings = await getCalendarBookings(businessId);
  return bookings.filter((b) => b.status === status);
}

/**
 * Get pending bookings that need confirmation
 *
 * @param businessId - Optional business ID filter
 * @returns Pending bookings
 */
export async function getPendingBookings(businessId?: string): Promise<CalendarBooking[]> {
  return getBookingsByStatus('PENDING', businessId);
}

/**
 * Get confirmed bookings
 *
 * @param businessId - Optional business ID filter
 * @returns Confirmed bookings
 */
export async function getConfirmedBookings(businessId?: string): Promise<CalendarBooking[]> {
  return getBookingsByStatus('CONFIRMED', businessId);
}
